import { useEffect, useMemo, useRef } from 'react';
import {
	collectCustomDetailDefinitionsIdsFromSourceTemplate,
	PreFillableTemplateFieldShape,
} from '@dealstack/frontend/admin/pages/Manage/ManagePreFillableForms/common/utils.ts';
import { difference, isEmpty } from 'lodash';
import { CustomDetailDefinitionSource } from '@dealstack/frontend/admin/pages/Manage/common/EditFieldMappingModal/availableSources.ts';
import { SmartFieldInForm } from '@dealstack/frontend/admin/pages/Manage/ManageGeneratableDocuments/EditGeneratableDocument/SmartFieldsTab/useSmartFieldsController.ts';

export const useSmartFieldsHighlight = (
	smartFields: SmartFieldInForm[],
	customDetailsDefinitionsSources: CustomDetailDefinitionSource[] = [],
	setSelectedSmartField: (field: SmartFieldInForm | null) => void
) => {
	const pdfViewerRef = useRef<HTMLDivElement>(null);

	const smartFieldsValues = smartFields.map((field) => field.fieldSourceTemplate);

	const validatedFields: (PreFillableTemplateFieldShape & { isValid?: boolean })[] =
		useMemo(() => {
			const customDetailsDefinitionsIds =
				customDetailsDefinitionsSources?.map((d) => d.id) ?? [];

			return smartFields.map((field) => {
				if (!field.fieldSourceTemplate) {
					return field;
				}

				const customDetailDefinitionsIdsInUse =
					collectCustomDetailDefinitionsIdsFromSourceTemplate(field.fieldSourceTemplate);

				const missingIds = difference(
					customDetailDefinitionsIdsInUse,
					customDetailsDefinitionsIds
				);

				return { ...field, isValid: isEmpty(missingIds) };
			});
		}, [smartFieldsValues, customDetailsDefinitionsSources]);

	const higlightSmartFields = () => {
		if (!pdfViewerRef.current) {
			return;
		}

		const pdfSpans = Array.from(
			pdfViewerRef.current.querySelectorAll('span[role="presentation"]')
		) as HTMLSpanElement[];
		if (!pdfSpans.length) {
			return;
		}

		for (const smartField of validatedFields) {
			const spansWithSmartField = pdfSpans.filter((el: HTMLSpanElement) =>
				el.innerText?.trim().includes(smartField.fieldKey)
			);

			let className = 'smartField';
			if (smartField.fieldSourceTemplate) {
				className = smartField.isValid
					? `${className} smartField-valid`
					: `${className} smartField-invalid`;
			}

			for (const spanWithSmartField of spansWithSmartField) {
				const text = spanWithSmartField.innerText;
				const replaced = text.replace(
					`{{${smartField.fieldKey}}}`,
					`<span class="${className}" data-field_key="${smartField.fieldKey}">{{${smartField.fieldKey}}}</span>`
				);
				spanWithSmartField.innerHTML = replaced;
			}
		}

		const handler = (e: MouseEvent) => {
			const target = e.target as HTMLElement;

			if (target.tagName === 'SPAN' && target.classList.contains('smartField')) {
				const smartField = smartFields.find(
					(smartField) => smartField.fieldKey === target.dataset.field_key
				);

				setSelectedSmartField(smartField ?? null);
			}
		};

		pdfViewerRef.current.addEventListener('click', handler);

		return () => pdfViewerRef.current?.removeEventListener('click', handler);
	};

	useEffect(higlightSmartFields, [validatedFields, setSelectedSmartField]);

	const generatedFileNameField = useMemo(() => {
		return validatedFields.find((field) => field.fieldKey === 'generatedFileName');
	}, [validatedFields]);

	return { pdfViewerRef, higlightSmartFields, generatedFileNameField };
};
